"use client";

import { useEffect } from "react";

/**
 * Empêche l'écran de s'éteindre tant que `active` est vrai.
 *
 * Motivation, très concrète : une tablette posée sur le pupitre s'éteint toute
 * seule pendant qu'on travaille vingt minutes sur le vrai piano. Rien dans la
 * page ne compte comme une interaction, puisque les mains sont sur les touches
 * et non sur l'écran. C'est le défaut rédhibitoire de la posture de référence,
 * et le verrou de réveil est exactement ce qui le corrige.
 *
 * Trois pièges de l'API, tous traités ici :
 *
 * - **Le verrou est relâché par le navigateur dès que le document passe en
 *   arrière-plan**, et il ne revient pas tout seul. Sans réacquisition sur
 *   `visibilitychange`, un simple passage dans une autre application suffit à
 *   perdre le verrou pour le reste de la session.
 * - **`request()` rejette dans des cas parfaitement normaux** : API absente
 *   (Safari a longtemps été le cas), batterie faible, onglet non visible,
 *   politique de permission. Aucun de ces cas ne mérite un message : le pire
 *   qui arrive est que l'écran s'éteigne comme avant. On échoue donc en
 *   silence, sans jamais rejeter vers l'appelant.
 * - **La demande est asynchrone** : l'effet peut être démonté avant qu'elle
 *   n'aboutisse. Le drapeau `released` sert à relâcher un verrou arrivé trop
 *   tard, faute de quoi l'écran resterait allumé après avoir quitté le studio.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active || typeof navigator === "undefined" || !("wakeLock" in navigator)) {
      return;
    }

    let sentinel: WakeLockSentinel | null = null;
    let released = false;

    const acquire = async (): Promise<void> => {
      // Le navigateur refuse la demande quand le document est caché ; inutile
      // de la tenter, `visibilitychange` la rejouera au retour.
      if (document.visibilityState !== "visible" || sentinel !== null) {
        return;
      }

      try {
        const next = await navigator.wakeLock.request("screen");

        if (released) {
          void next.release();

          return;
        }

        sentinel = next;
        // Le verrou peut tomber sans qu'on l'ait demandé (mise en veille
        // système, batterie). On oublie la sentinelle pour que la prochaine
        // réacquisition ne se croie pas déjà servie.
        next.addEventListener("release", () => {
          if (sentinel === next) {
            sentinel = null;
          }
        });
      } catch {
        // Verrou non accordé : l'écran s'éteindra, comme avant. Rien à dire.
      }
    };

    const handleVisibility = (): void => {
      void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      released = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      void sentinel?.release();
      sentinel = null;
    };
  }, [active]);
}
